import type { ReactNode, Ref } from "react";
import { Link, useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";
import type { Conversation, MyCommunity } from "@atrium/shared";
import { api } from "../lib/api";
import { cn } from "../lib/cn";
import { timeAgo } from "../lib/format";
import { useRail } from "../features/rail/useRail";
import { CommunityContextMenu } from "../features/rail/RailMenus";
import { CommunityIcon } from "../components/ui/Avatar";
import { IconButton } from "../components/ui/Button";
import { Badge } from "../components/ui/Controls";
import { Skeleton } from "../components/ui/Skeleton";
import { Tooltip } from "../components/ui/Tooltip";
import { IconCompose, IconVolume } from "../components/icons";
import { useAuth } from "../features/auth/AuthProvider";
import { DiscoverSidebar } from "../features/feed/DiscoverSidebar";
import { PanelSection } from "../features/feed/PanelSection";
import { ConversationAvatar, conversationTitle } from "../features/messages/MessagesPage";
import { encryptedPreview } from "../features/messages/e2eeMessages";

function HomeCommunity({ c, active }: { c: MyCommunity; active: boolean }) {
  return (
    <li>
      <CommunityContextMenu community={c}>
        <Link to={`/c/${c.id}`} className={cn("flex h-11 items-center gap-3 rounded-lg px-2 transition-colors hover:bg-surface", active && "bg-surface-2")}>
          <CommunityIcon src={c.iconUrl} name={c.name} size="sm" />
          <span className={cn("min-w-0 flex-1 truncate text-sm", c.unread ? "font-semibold text-fg" : "text-fg-2")}>{c.name}</span>
          {c.voiceCount > 0 && (
            <Tooltip label={`${c.voiceCount} in voice`}>
              <span className="flex items-center gap-0.5 text-xs text-success tabular">
                <IconVolume className="icon size-3.5" aria-hidden />
                {c.voiceCount}
              </span>
            </Tooltip>
          )}
          {c.mentionCount > 0 ? <Badge count={c.mentionCount} /> : c.unread ? <Badge dot /> : null}
        </Link>
      </CommunityContextMenu>
    </li>
  );
}

/** Communities that need attention: mentions first, then voice activity, then unread. */
function activeCommunities(list: MyCommunity[] | undefined) {
  const score = (c: MyCommunity) => (c.mentionCount > 0 ? 4 : 0) + (c.voiceCount > 0 ? 2 : 0) + (c.unread ? 1 : 0);
  return (list ?? []).filter((c) => score(c) > 0).sort((a, b) => score(b) - score(a) || b.mentionCount - a.mentionCount);
}

/**
 * Left panel on wide screens: what's happening where you belong. The rail
 * already lists every community, so this only shows the ones with something
 * going on (mentions, people in voice, unread), followed by conversations.
 */
export function HomePanel() {
  const { pathname } = useLocation();
  const { communities } = useRail();
  const conversations = useQuery({ queryKey: ["conversations"], queryFn: () => api.get<{ items: Conversation[] }>("/api/conversations").then((r) => r.items) });
  const { me } = useAuth();
  if (!me) return null;
  const active = activeCommunities(communities);
  return (
    <div className="divide-y divide-line">
      {active.length > 0 && (
        <PanelSection title="Active now">
          <ul className="space-y-px" aria-label="Active communities">
            {active.slice(0, 8).map((c) => (
              <HomeCommunity key={c.id} c={c} active={pathname.startsWith(`/c/${c.id}`)} />
            ))}
          </ul>
        </PanelSection>
      )}
      <PanelSection
        title="Messages"
        action={
          <Link to="/messages" aria-label="Open messages">
            <IconButton size="sm" label="Open messages" icon={IconCompose} tooltip={false} />
          </Link>
        }
      >
        {conversations.isPending ? (
          <div className="space-y-2 px-2">
            <Skeleton className="h-11" />
            <Skeleton className="h-11" />
          </div>
        ) : !conversations.data?.length ? (
          <p className="px-2 py-2 text-sm text-fg-3">
            No conversations yet.{" "}
            <Link to="/messages" className="text-accent hover:underline">
              Start one
            </Link>
          </p>
        ) : (
          <ul className="space-y-px">
            {conversations.data.slice(0, 12).map((c) => (
              <li key={c.id}>
                <Link to={`/messages/${c.id}`} className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-surface">
                  <ConversationAvatar c={c} meId={me.id} size="md" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className={cn("truncate text-sm", c.unreadCount ? "font-semibold text-fg" : "text-fg-2")}>{conversationTitle(c, me.id)}</span>
                      {c.lastMessage && <span className="shrink-0 text-xs text-fg-3">{timeAgo(c.lastMessage.createdAt)}</span>}
                    </div>
                    <div className="truncate text-xs text-fg-3">{c.lastMessage ? c.lastMessage.content || (c.lastMessage.ciphertext ? encryptedPreview(c.lastMessage) : "Image") : "No messages yet"}</div>
                  </div>
                  {c.unreadCount > 0 && <Badge count={c.unreadCount} className="bg-accent text-accent-fg" />}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </PanelSection>
    </div>
  );
}

/**
 * Responsive page frame.
 * - ≥1280px: [home panel | content | discovery] — side panels absorb extra width.
 * - ≥1024px: [content | discovery].
 * - below: content only.
 */
export function PageFrame({ children, left, right, className, scrollRef }: { children: ReactNode; left?: ReactNode | false; right?: ReactNode | false; className?: string; scrollRef?: Ref<HTMLDivElement> }) {
  const { me } = useAuth();
  const leftContent = left === undefined ? me ? <HomePanel /> : null : left || null;
  const rightContent = right === undefined ? <DiscoverSidebar /> : right || null;
  return (
    <div ref={scrollRef} className={cn("scroll-y h-full", className)}>
      <div
        className={cn(
          "grid min-h-full grid-cols-1",
          rightContent && "lg:grid-cols-[minmax(0,1fr)_minmax(320px,380px)]",
          rightContent && leftContent && "xl:grid-cols-[minmax(280px,380px)_minmax(0,1fr)_minmax(320px,400px)]",
          !rightContent && leftContent && "xl:grid-cols-[minmax(280px,380px)_minmax(0,1fr)]",
        )}
      >
        {leftContent && (
          <aside aria-label="Your spaces" className="sticky top-0 hidden h-dvh overflow-y-auto overscroll-contain border-r border-line bg-bg-subtle xl:block">
            {leftContent}
          </aside>
        )}
        <div className="min-w-0 border-line lg:border-r">{children}</div>
        {rightContent && (
          <aside aria-label="Discover" className="sticky top-0 hidden h-dvh overflow-y-auto overscroll-contain bg-bg-subtle lg:block">
            {rightContent}
          </aside>
        )}
      </div>
    </div>
  );
}
